import { useEffect } from "react";
import { useNavigate } from "react-router";
import { useGoogleCategoriesStore } from "~/lib/stores/google-categories";
import { cn } from "~/lib/utils";

export interface SubcategoryScrollProps {
  rootCategoryId: string;
  subCategoryId?: string;
}

export default function SubcategoryScroll({ rootCategoryId, subCategoryId }: SubcategoryScrollProps) {
  const navigate = useNavigate();
  const { selectedCategoryId, setSelectedCategory, getSubcategories, isLoading } = useGoogleCategoriesStore();

  useEffect(() => {
    if (rootCategoryId && rootCategoryId !== selectedCategoryId) {
      setSelectedCategory(rootCategoryId);
    }
  }, [rootCategoryId, selectedCategoryId, setSelectedCategory]);

  const subcategories = rootCategoryId ? getSubcategories(rootCategoryId) || [] : [];

  const handleSubcategoryClick = (categoryId: string) => {
    navigate(`/shop/category?categoryId=${encodeURIComponent(categoryId)}`);
  };

  if (isLoading && subcategories.length === 0) {
    // Loading skeleton
    return (
      <div className="scrollbar-hide w-full overflow-x-auto">
        <div className="flex gap-[24px]">
          {Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="flex shrink-0 flex-col items-center gap-[8px]">
              <div className="size-[100px] animate-pulse overflow-hidden rounded-[16px] border border-[#dddddd] bg-gray-200" />
              <div className="h-[18px] w-[100px] animate-pulse rounded bg-gray-200" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (subcategories.length === 0) {
    return null;
  }

  return (
    <div className="scrollbar-hide w-full overflow-x-auto">
      <div className="flex gap-[24px]">
        {subcategories.map(subcategory => (
          <div
            key={subcategory.categoryId}
            role="button"
            tabIndex={0}
            onClick={() => handleSubcategoryClick(subcategory.categoryId)}
            onKeyDown={e => e.key === "Enter" && handleSubcategoryClick(subcategory.categoryId)}
            className={cn(
              "flex shrink-0 cursor-pointer flex-col items-center transition-opacity hover:opacity-80",
              subCategoryId === subcategory.categoryId && "opacity-100"
            )}
          >
            <div className="size-[100px] overflow-hidden rounded-[16px] border border-[#dddddd] bg-gray-50">
              {subcategory.imageUrl ? (
                <img
                  src={subcategory.imageUrl}
                  alt={subcategory.category || ""}
                  className="size-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="flex size-full items-center justify-center bg-gray-100">
                  <span className="text-[32px]">📦</span>
                </div>
              )}
            </div>
            <div className="flex h-[48px] w-[100px] items-center justify-center">
              <p className="line-clamp-2 text-center text-[12px] leading-[18px] font-medium text-neutral-700">
                {subcategory.category || "Unknown"}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
